import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';
import offer, { CALL_PORTRAIT, euroAmount, isFilled } from '../config/offer';
import { formatCallSlot, loadCallWindow, saveCallWindow } from '../lib/callSlots';
import { SolanaPayButton } from './SolanaPay';
import COPY from './openBroV50Copy';
import './OpenBroV50.css';

/**
 * OpenBro /v50 : ordre des sections de university.com, vidéo en tête, texte original (openBroV50Copy.js).
 * Créneaux, compte à rebours et paiement repris de OpenBroHome.jsx. L'urgence ne s'appuie que sur
 * des faits du code : créneaux réels, réservables jusqu'à 1 h 30 avant, 49 €/mois.
 */

const SUPPORT_EMAIL = 'mia@openpro.ai';

/** Réservable jusqu'à 1 h 30 avant l'heure du créneau (même règle que lib/callSlots). */
const BOOKING_LEAD_MS = 90 * 60 * 1000;
/** Modale centrale : une fois par session, ~45 s après l'arrivée. */
const NUDGE_KEY = 'openbro_v50_nudge';
const NUDGE_DELAY_MS = 45 * 1000;

// Couvertures « CAMPUS » de l'espace membre (Dashboard.jsx). Leçons : src/data/formations.js.
const CAMPUS = [
  { id: 'musculation', src: '/v10/campus-musculation.jpg', lessons: 31 },
  { id: 'nutrition', src: '/v10/campus-nutrition.jpg', lessons: 16 },
  { id: 'reseaux', src: '/v10/campus-reseaux.jpg', lessons: 18 },
  { id: 'montage', src: '/v10/campus-montage.jpg', lessons: 16 },
  { id: 'ia', src: '/v10/campus-ia.jpg', lessons: 12 },
  { id: 'filming', src: '/v10/campus-filming.jpg', lessons: 16 },
];
const TOTAL_LESSONS = CAMPUS.reduce((sum, item) => sum + item.lessons, 0);

// Reels 9:16 (mises en situation, cf. FAQ).
const STORIES = [
  { id: 'adam', src: '/ugc/15-adam-23-salle.mp4', poster: '/ugc/15-adam-23-salle.jpg' },
  { id: 'ines', src: '/ugc/12-ines-24-assiette.mp4', poster: '/ugc/12-ines-24-assiette.jpg' },
  { id: 'theo', src: '/ugc/16-theo-25-business.mp4', poster: '/ugc/16-theo-25-business.jpg' },
  { id: 'lucas', src: '/ugc/14-lucas-21-vie.mp4', poster: '/ugc/14-lucas-21-vie.jpg' },
];

const FEATURE_IMAGES = ['/cover-train.jpg', '/v10/v20-iphone-bros.webp', '/portrait-gaut.jpg'];

// Captures du mur de la home (studentWinsData.wins de mock.js), sans texte ajouté.
const WIN_SHOTS = ['15', '04', '07', '08', '01', '12', '14', '02', '11', '03', '06', '09', '10', '05'].map((n) => `/v10/wins/win-${n}.jpg`);

function Icon({ name }) {
  const paths = {
    check: 'M5 12.5l4.5 4.5L19 7.5',
    cross: 'M7 7l10 10M17 7L7 17',
    lock: 'M8 11V8a4 4 0 0 1 8 0v3M6.5 11h11v9h-11z',
    arrow: 'M5 12h14M13 6l6 6-6 6',
    plus: 'M12 5v14M5 12h14',
    play: 'M9 6.5v11l9-5.5z',
    clock: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 7.5V12l3 2',
    call: 'M7 4h3l1 4-2 1a12 12 0 0 0 6 6l1-2 4 1v3a2 2 0 0 1-2 2A16 16 0 0 1 5 6a2 2 0 0 1 2-2z',
    globe: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM3.5 12h17M12 3.5c2.2 2.4 2.2 14.6 0 17M12 3.5c-2.2 2.4-2.2 14.6 0 17',
  };
  return (
    <svg className="ov-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d={paths[name]} />
    </svg>
  );
}

/** `**mot**` mis en avant ; `\n` = retour à la ligne. */
function Rich({ text }) {
  return text.split('\n').map((line, row) => (
    <React.Fragment key={row}>
      {row > 0 && <br />}
      {line.split('**').map((part, index) => (index % 2 ? <strong key={index}>{part}</strong> : <React.Fragment key={index}>{part}</React.Fragment>))}
    </React.Fragment>
  ));
}

/** « 59 min 12 s » / « 2 h 14 min 03 s » / « 1 j 3 h » ; null si la limite est passée. */
function timeLeft(ms, fmt) {
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const ss = String(total % 60).padStart(2, '0');
  if (days) return `${days} ${fmt.day} ${hours} ${fmt.hour}`;
  if (hours) return `${hours} ${fmt.hour} ${String(mins).padStart(2, '0')} ${fmt.min} ${ss} ${fmt.sec}`;
  if (mins) return `${mins} ${fmt.min} ${ss} ${fmt.sec}`;
  return `${total % 60} ${fmt.sec}`;
}

function Pill({ children, hot }) {
  return <p className={hot ? 'ov-pill ov-pill-hot' : 'ov-pill'}>{children}</p>;
}

function PayMarks() {
  return (
    <span className="ov-marks" aria-hidden="true">
      <span>Apple Pay</span>
      <span>VISA</span>
      <span>Mastercard</span>
      <span>CB</span>
    </span>
  );
}

/** Reels muets, lus seulement quand la rangée est visible. */
function Stories({ copy }) {
  const listRef = useRef(null);
  const videos = useRef({});

  useEffect(() => {
    const node = listRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const observer = new IntersectionObserver(([entry]) => {
      Object.values(videos.current).forEach((video) => {
        if (!video) return;
        if (entry.isIntersecting && !calm) video.play().catch(() => {});
        else if (!entry.isIntersecting) video.pause();
      });
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <ul className="ov-stories" ref={listRef}>
      {STORIES.map((story) => (
        <li key={story.id}>
          <div className="ov-story-media">
            <video
              ref={(node) => { videos.current[story.id] = node; }}
              src={story.src}
              poster={story.poster}
              muted
              loop
              playsInline
              preload="none"
            />
            <span className="ov-story-tag">{copy.stories[story.id][0]}</span>
          </div>
          <p className="ov-story-win">{copy.stories[story.id][1]}</p>
        </li>
      ))}
    </ul>
  );
}

/** Bas-gauche : rotation toutes les 6 s. Fermé, il revient 60 s plus tard. */
function Toasts({ items, onPay, disabled, closeLabel }) {
  const [index, setIndex] = useState(0);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((i) => i + 1), 6000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!closed) return undefined;
    const timer = window.setTimeout(() => setClosed(false), 60000);
    return () => window.clearTimeout(timer);
  }, [closed]);
  if (closed || !items.length) return null;
  const at = index % items.length;
  const [title, line, foot] = items[at];
  return (
    <aside className="ov-toast" key={at}>
      <button type="button" className="ov-toast-pay" disabled={disabled} onClick={onPay}>
        <img src={CALL_PORTRAIT} alt="" />
        <span className="ov-toast-title">{title}</span>
        <span className="ov-toast-line">{line}</span>
        <span className="ov-toast-foot">{foot}</span>
      </button>
      <button type="button" className="ov-x" onClick={() => setClosed(true)} aria-label={closeLabel}>×</button>
    </aside>
  );
}

/** Haut-droite : passe d'un vrai créneau à l'autre toutes les 10 s, décalé du toast. Clic = réserve ce créneau. */
function SlotPeek({ slots, selectedIso, copy, onBook, disabled }) {
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState(false);
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    const start = window.setTimeout(() => setShown(true), 3000);
    const timer = window.setInterval(() => setIndex((i) => i + 1), 10000);
    return () => { window.clearTimeout(start); window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (!closed) return undefined;
    const timer = window.setTimeout(() => setClosed(false), 60000);
    return () => window.clearTimeout(timer);
  }, [closed]);
  if (!shown || closed || !slots.length) return null;
  const slot = slots[index % slots.length];
  const mine = slot.iso === selectedIso;
  return (
    <aside className="ov-peek" key={slot.iso}>
      <button type="button" className="ov-peek-pay" disabled={disabled} onClick={() => onBook(slot)}>
        <span className="ov-peek-ico"><Icon name="call" /></span>
        <span>
          <em>{mine ? copy.peekMine : copy.peekOther}</em>
          <strong>{slot.label}</strong>
          <small>{copy.peekFoot}</small>
        </span>
      </button>
      <button type="button" className="ov-x" onClick={() => setClosed(true)} aria-label={copy.close}>×</button>
    </aside>
  );
}

export default function OpenBroV50() {
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const copy = COPY[lang] || COPY.fr;
  const price = euroAmount(offer.price);
  const [callWindow, setCallWindow] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [showBar, setShowBar] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [filmOn, setFilmOn] = useState(false);
  const [filmTime, setFilmTime] = useState(0);
  const [filmDuration, setFilmDuration] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const heroCtaRef = useRef(null);
  const filmRef = useRef(null);

  const slots = (callWindow?.slots || []).map((iso) => formatCallSlot(iso, lang));
  const spansDays = new Set(slots.map((slot) => slot.day)).size > 1;
  const selected = slots.find((slot) => slot.iso === callWindow?.selected) || slots[0] || null;

  useEffect(() => {
    setCallWindow(loadCallWindow());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  // Créneau passé sous la limite de réservation : on recharge une fenêtre fraîche (vrais créneaux).
  useEffect(() => {
    if (callWindow && selected && Date.parse(selected.iso) - BOOKING_LEAD_MS <= now) setCallWindow(loadCallWindow());
  }, [now, callWindow, selected]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = copy.title;
  }, [lang, copy.title]);

  // Vidéo en tête : chargée tout de suite, muette tant qu'elle est visible, voile jusqu'au clic.
  useEffect(() => {
    const video = filmRef.current;
    if (!video || filmOn) return undefined;
    let visible = false;
    const playMuted = () => {
      if (!visible) return;
      video.muted = true;
      video.play().catch(() => {});
    };
    video.preload = 'auto';
    video.load();
    video.addEventListener('canplay', playMuted);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) playMuted();
      else video.pause();
    }, { threshold: 0.15 });
    observer.observe(video);
    return () => {
      video.removeEventListener('canplay', playMuted);
      observer.disconnect();
    };
  }, [filmOn]);

  useEffect(() => {
    const node = heroCtaRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setShowBar(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(NUDGE_KEY) === '1') return undefined;
    const timer = window.setTimeout(() => {
      sessionStorage.setItem(NUDGE_KEY, '1');
      setNudge(true);
    }, NUDGE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!nudge) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setNudge(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nudge]);

  const startFilm = () => {
    const video = filmRef.current;
    if (!video) return;
    setFilmOn(true);
    video.loop = false;
    video.muted = false;
    const playFromStart = () => {
      video.removeEventListener('seeked', playFromStart);
      video.play().catch(() => {});
    };
    if (video.currentTime > 0.05) {
      video.addEventListener('seeked', playFromStart);
      video.currentTime = 0;
      return;
    }
    video.play().catch(() => {});
  };

  const toggleFilm = () => {
    const video = filmRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  };

  const seekFilm = (event) => {
    const video = filmRef.current;
    if (!video || !filmDuration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    video.currentTime = ratio * filmDuration;
  };

  const chooseSlot = (iso) => {
    setCallWindow((current) => {
      if (!current) return current;
      const next = { ...current, selected: iso };
      saveCallWindow(next);
      return next;
    });
  };

  /** Paiement avec le créneau choisi (ou celui passé en argument, depuis la popup haut-droite). */
  const bookCall = (source, slot = selected) => {
    if (isActive) return;
    setNudge(false);
    if (!slot) {
      if (isFilled(offer.paymentUrl)) window.location.assign(offer.paymentUrl);
      else startCheckout(source);
      return;
    }
    if (slot.iso !== selected?.iso) chooseSlot(slot.iso);
    startCheckout(source, {
      callAt: slot.iso,
      callLabel: slot.label,
      callTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const left = selected ? timeLeft(Date.parse(selected.iso) - BOOKING_LEAD_MS - now, copy.leftFmt) : null;
  const ctaLabel = selected ? copy.takeSlot(selected.label) : copy.heroCta;

  const cta = (source, extra = '', ref = undefined) => (
    <button type="button" ref={ref} className={`ov-btn ov-btn-lg ov-pulse ${extra}`} disabled={isActive} onClick={() => bookCall(source)}>
      {isActive ? '…' : ctaLabel} <Icon name="arrow" />
    </button>
  );

  const ctaBlock = (source, extra = '') => (
    <div className={`ov-cta-block ${extra}`}>
      {cta(source)}
      {left && (
        <button type="button" className="ov-left" disabled={isActive} onClick={() => bookCall(`${source}-left`)}>
          <Icon name="clock" />{copy.barLeft(left)}
        </button>
      )}
      <p className="ov-pay-note">{copy.payNote(price)}</p>
    </div>
  );

  return (
    <div className="ov">
      <header className="ov-header">
        <div className="ov-wrap ov-header-row">
          <a className="ov-logo" href="#top"><img src="/logo-openbro.png" alt="OpenBro" /></a>
          <div className="ov-header-actions">
            <button type="button" className="ov-lang" onClick={toggleLang} aria-label={copy.switchLabel}>
              <Icon name="globe" />{copy.lang}
            </button>
            <Link className="ov-login" to="/login">{copy.login}</Link>
            <button type="button" className="ov-btn ov-btn-sm" disabled={isActive} onClick={() => bookCall('v50-header')}>
              {selected ? copy.headerSlot(selected.time) : copy.headerCta}
            </button>
          </div>
        </div>
        {selected && left && (
          <button type="button" className="ov-band" disabled={isActive} onClick={() => bookCall('v50-band')}>
            <span className="ov-dot" aria-hidden="true" />
            <span>{copy.bandKicker} · <strong>{selected.label}</strong></span>
            <span className="ov-band-left">{copy.bandLeft(left)}</span>
          </button>
        )}
      </header>

      {/* 0 · Vidéo, avant tout le reste */}
      <section className="ov-film-hero" id="top">
        <div className="ov-film" onContextMenu={(event) => event.preventDefault()}>
          <video
            ref={filmRef}
            src="/openbro-film-fr.mp4"
            poster="/openbro-film-fr.jpg"
            muted
            loop={!filmOn}
            controls={false}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            playsInline
            preload="auto"
            onContextMenu={(event) => event.preventDefault()}
            onTimeUpdate={(event) => setFilmTime(event.currentTarget.currentTime)}
            onLoadedMetadata={(event) => setFilmDuration(event.currentTarget.duration)}
            onClick={() => { if (filmOn) toggleFilm(); }}
          />
          {!filmOn && <div className="ov-film-veil" aria-hidden="true" />}
          {!filmOn && (
            <button type="button" className="ov-film-play" aria-label={copy.filmPlay} onClick={startFilm}>
              <Icon name="play" />
            </button>
          )}
          {filmOn && (
            <button type="button" className="ov-film-line" onClick={seekFilm} aria-label={copy.filmProgress}>
              <i style={{ width: `${filmDuration ? (filmTime / filmDuration) * 100 : 0}%` }} />
            </button>
          )}
        </div>
      </section>

      {/* 1 · Hero */}
      <section className="ov-hero">
        <div className="ov-wrap ov-hero-grid">
          <div>
            <Pill>{copy.heroPill}</Pill>
            <h1 className="ov-h1"><Rich text={copy.heroTitle} /></h1>
            <p className="ov-lead"><Rich text={copy.heroLead} /></p>
            <div className="ov-cta-block">
              {cta('v50-hero', '', heroCtaRef)}
              {left && (
                <button type="button" className="ov-left" disabled={isActive} onClick={() => bookCall('v50-hero-left')}>
                  <Icon name="clock" />{copy.barLeft(left)}
                </button>
              )}
              <p className="ov-pay-note">{copy.payNote(price)}</p>
            </div>
          </div>
          <aside className="ov-book" aria-labelledby="ov-book-title">
            <div className="ov-book-head">
              <img src={CALL_PORTRAIT} alt="" />
              <div>
                <p className="ov-live">{copy.lastPill}</p>
                <h2 id="ov-book-title">{copy.slotsLabel}</h2>
              </div>
            </div>
            <p className="ov-book-body">{copy.slotsBody}</p>
            <div className="ov-slots" role="radiogroup" aria-label={copy.slotsLabel}>
              {slots.map((slot) => (
                <button
                  key={slot.iso}
                  type="button"
                  role="radio"
                  aria-checked={selected?.iso === slot.iso}
                  className={selected?.iso === slot.iso ? 'is-on' : undefined}
                  onClick={() => chooseSlot(slot.iso)}
                >
                  {spansDays ? slot.chip : slot.time}
                </button>
              ))}
            </div>
            <button type="button" className="ov-btn ov-btn-block" disabled={isActive} onClick={() => bookCall('v50-book')}>
              {isActive ? '…' : ctaLabel}
            </button>
          </aside>
        </div>
        <ul className="ov-wrap ov-stats">
          {copy.stats(TOTAL_LESSONS).map(([value, label]) => (
            <li key={label}><strong>{value}</strong><span>{label}</span></li>
          ))}
        </ul>
      </section>

      {/* 2 · Take action */}
      <section className="ov-section ov-alt">
        <div className="ov-wrap ov-split">
          <div>
            <Pill>{copy.actionPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.actionTitle} /></h2>
            <div className="ov-body">{copy.action.map((text) => <p key={text}><Rich text={text} /></p>)}</div>
            {ctaBlock('v50-action')}
          </div>
          <figure className="ov-device">
            <img src="/v10/home-iphone.jpg" alt="" loading="lazy" decoding="async" />
          </figure>
        </div>
      </section>

      {/* 3 · Campus */}
      <section className="ov-section" id="campus">
        <div className="ov-wrap">
          <div className="ov-center">
            <Pill>{copy.campusPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.campusTitle} /></h2>
            <p className="ov-sub"><Rich text={copy.campusBody} /></p>
          </div>
          <ul className="ov-campus">
            {CAMPUS.map((item) => {
              const [title, text] = copy.campus[item.id];
              return (
                <li key={item.id}>
                  <img src={item.src} alt="" loading="lazy" decoding="async" />
                  <div className="ov-campus-info">
                    <em>{copy.lessons(item.lessons)}</em>
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* 4 · Plateforme */}
      <section className="ov-section ov-alt">
        <div className="ov-wrap ov-split ov-split-rev">
          <div>
            <Pill>{copy.platformPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.platformTitle} /></h2>
            <div className="ov-body">{copy.platform.map((text) => <p key={text}><Rich text={text} /></p>)}</div>
          </div>
          <figure className="ov-imac">
            <button type="button" className="ov-imac-hit" disabled={isActive} onClick={() => bookCall('v50-vault')} aria-label={copy.vaultLock}>
              <img src="/v10/imac-float.webp" alt="" loading="lazy" />
              <span className="ov-imac-pill"><Icon name="lock" />{copy.vaultLock}</span>
            </button>
          </figure>
        </div>
      </section>

      {/* 5 · Témoignages (reels, mises en situation) */}
      <section className="ov-section">
        <div className="ov-wrap">
          <div className="ov-center">
            <Pill>{copy.storiesPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.storiesTitle} /></h2>
          </div>
          <Stories copy={copy} />
          <p className="ov-note">{copy.storiesNote}</p>
        </div>
      </section>

      {/* 6 · Triptyque, image et texte en alternance */}
      <section className="ov-section ov-alt">
        <div className="ov-wrap ov-features">
          {copy.features.map((feature, index) => (
            <article key={feature.pill} className={index % 2 ? 'ov-feature is-rev' : 'ov-feature'}>
              <figure><img src={FEATURE_IMAGES[index]} alt="" loading="lazy" decoding="async" /></figure>
              <div>
                <Pill>{feature.pill}</Pill>
                <h3 className="ov-h3"><Rich text={feature.title} /></h3>
                <p className="ov-body-p"><Rich text={feature.body} /></p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* 7 · Deux chemins */}
      <section className="ov-section" id="prix">
        <div className="ov-wrap">
          <div className="ov-center">
            <Pill>{copy.pathsPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.pathsTitle} /></h2>
          </div>
          <div className="ov-paths">
            <article className="ov-path ov-path-off">
              <h3>{copy.offTitle}</h3>
              <p className="ov-path-sub">{copy.offSub}</p>
              <ul>{copy.off.map((item) => <li key={item}><Icon name="cross" />{item}</li>)}</ul>
              <a className="ov-btn ov-btn-dim ov-btn-block" href={copy.offHref} target="_blank" rel="noopener noreferrer">{copy.offCta}</a>
            </article>
            <span className="ov-vs" aria-hidden="true">vs</span>
            <article className="ov-path ov-path-on">
              <h3><img src="/logo-openbro.png" alt="OpenBro" /></h3>
              <p className="ov-path-sub">{copy.onSub}</p>
              <p className="ov-price"><strong>{price}</strong><em>{copy.period}</em></p>
              <ul>{copy.on.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
              {cta('v50-paths', 'ov-btn-block')}
              {left && (
                <button type="button" className="ov-left ov-left-center" disabled={isActive} onClick={() => bookCall('v50-paths-left')}>
                  <Icon name="clock" />{copy.barLeft(left)}
                </button>
              )}
              <p className="ov-secure"><Icon name="lock" />{copy.secure}<PayMarks /></p>
              <SolanaPayButton lang={lang} className="ov-crypto" label={copy.crypto} />
            </article>
          </div>
        </div>
      </section>

      {/* 8 · Wins */}
      <section className="ov-section ov-alt">
        <div className="ov-wrap">
          <div className="ov-center">
            <Pill>{copy.winsPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.winsTitle} /></h2>
          </div>
          <ul className="ov-wins">
            {WIN_SHOTS.map((src) => (
              <li key={src}><img src={src} alt="" loading="lazy" decoding="async" /></li>
            ))}
          </ul>
          {ctaBlock('v50-wins', 'ov-cta-center')}
        </div>
      </section>

      {/* 9 · Dernière ligne avant la FAQ */}
      <section className="ov-last">
        <div className="ov-wrap">
          <Pill hot>{copy.lastPill}</Pill>
          <h2 className="ov-h2"><Rich text={copy.lastTitle} /></h2>
          <p className="ov-sub"><Rich text={copy.lastBody} /></p>
          {selected && left && (
            <button type="button" className="ov-last-slot" disabled={isActive} onClick={() => bookCall('v50-last-slot')}>
              <Icon name="clock" /><span><Rich text={copy.lastLeft({ label: selected.label, left })} /></span>
            </button>
          )}
          {ctaBlock('v50-last', 'ov-cta-center')}
        </div>
      </section>

      {/* 10 · FAQ */}
      <section className="ov-section" id="faq">
        <div className="ov-wrap ov-faq-grid">
          <div>
            <Pill>{copy.faqPill}</Pill>
            <h2 className="ov-h2"><Rich text={copy.faqTitle} /></h2>
            <p className="ov-sub">{copy.faqMore} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          </div>
          <div>
            {copy.faq.map(([question, answer], index) => {
              const open = openFaq === index;
              return (
                <div key={question} className={open ? 'ov-qa is-open' : 'ov-qa'}>
                  <button type="button" aria-expanded={open} onClick={() => setOpenFaq(open ? null : index)}>
                    {question}<Icon name="plus" />
                  </button>
                  {open && <p><Rich text={answer} /></p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 11 · Footer */}
      <footer className="ov-footer">
        <div className="ov-wrap ov-footer-row">
          <div>
            <img className="ov-footer-logo" src="/logo-openbro.png" alt="OpenBro" />
            <p>{copy.footerTag}</p>
          </div>
          <nav aria-label="Legal">
            <Link to="/terms">{copy.terms}</Link>
            <Link to="/privacy">{copy.privacy}</Link>
            <Link to="/login">{copy.login}</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </nav>
          <p className="ov-copy">© {new Date().getFullYear()} OpenBro. {copy.rights}</p>
        </div>
      </footer>

      <div className={showBar ? 'ov-bar is-on' : 'ov-bar'} aria-hidden={!showBar}>
        {selected ? (
          <span className="ov-bar-slot">
            <em>{copy.barSlot}</em>
            <strong>{selected.label}</strong>
            {left && (
              <button type="button" className="ov-bar-left" tabIndex={showBar ? 0 : -1} disabled={isActive} onClick={() => bookCall('v50-bar-left')}>
                {copy.barLeft(left)}
              </button>
            )}
          </span>
        ) : <span className="ov-price"><strong>{price}</strong><em>{copy.period}</em></span>}
        <button type="button" className="ov-btn ov-pulse" tabIndex={showBar ? 0 : -1} disabled={isActive} onClick={() => bookCall('v50-bar')}>
          {isActive ? '…' : `${copy.barTake} · ${price}`} <Icon name="arrow" />
        </button>
      </div>

      <Toasts
        items={copy.toasts({ label: selected?.label || '—', left, price })}
        disabled={isActive}
        closeLabel={copy.close}
        onPay={() => bookCall('v50-toast')}
      />
      <SlotPeek slots={slots} selectedIso={selected?.iso} copy={copy} disabled={isActive} onBook={(slot) => bookCall('v50-peek', slot)} />

      {nudge && selected && (
        <div className="ov-nudge" role="dialog" aria-modal="true" aria-labelledby="ov-nudge-title" onClick={() => setNudge(false)}>
          <div className="ov-nudge-card" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="ov-x" onClick={() => setNudge(false)} aria-label={copy.close}>×</button>
            <img src={CALL_PORTRAIT} alt="" />
            <p className="ov-live">{copy.nudgeKicker}</p>
            <h2 id="ov-nudge-title">{selected.label}</h2>
            {left && <p className="ov-nudge-left">{copy.nudgeLeft(left)}</p>}
            {cta('v50-nudge', 'ov-btn-block')}
            <p className="ov-pay-note">{copy.payNote(price)}</p>
            <button type="button" className="ov-nudge-later" onClick={() => setNudge(false)}>{copy.nudgeLater}</button>
          </div>
        </div>
      )}

      <style>{'html { scroll-behavior: smooth; } @media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }'}</style>
    </div>
  );
}
